import {IllustratedMessage} from '@common/ui/images/illustrated-message';
import {ErrorIcon} from '@common/icons/material/Error';
import {Button} from '@common/ui/buttons/button';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';
import {BootstrapData} from '@common/core/bootstrap-data/bootstrap-data';

interface PageLoadErrorFallbackProps {
  // swapped in tests, where jsdom cannot navigate
  reload?: () => void;
}

/**
 * Last-resort screen for the top-level error boundary.
 *
 * It renders after React has unmounted every app provider, so it must not
 * reach for context. <PageErrorMessage> does, through <Trans>, and when the
 * boundary caught an error the fallback itself crashed on the missing
 * bootstrap context: React then unmounted the whole page and visitors saw a
 * blank white screen. The lines are read straight from the bootstrap cache
 * instead, and a reload is offered because the usual cause, a route chunk
 * that failed to download, is fixed by one.
 */
export function PageLoadErrorFallback({
  reload = () => window.location.reload(),
}: PageLoadErrorFallbackProps) {
  return (
    <IllustratedMessage
      className="mt-40"
      image={
        <div>
          <ErrorIcon size="xl" />
        </div>
      }
      imageHeight="h-auto"
      title={line('There was an issue loading this page')}
      description={line('Please try again later')}
      action={
        <Button variant="flat" color="primary" onClick={reload}>
          {line('Reload page')}
        </Button>
      }
    />
  );
}

// the lookup useTrans() does, without the hook; when even the bootstrap data
// never loaded there are no lines and the English text shows
function line(message: string): string {
  const lines = (getBootstrapData() as BootstrapData | undefined)?.i18n?.lines;
  return lines?.[message] || lines?.[message.toLowerCase()] || message;
}
